import { useActiveProject } from "@/features/project/ActiveProject";
import { associateOrders } from "@repo/api";
import { t } from "@repo/translation";
import { ModalSheet, SectionContainer, useCustomer, useToast } from "@repo/ui";
import {
  getDebugMessage,
  trackAnalytics,
  useTypesafeActions,
} from "@repo/utils";
import { useState } from "react";

import { useSearchCustomerOrders } from "../../hooks/searchOrder";
import {
  type AddOrderState,
  type AddOrderStateActions,
  AddOrderStateDefault,
  addOrderStateActions,
  addOrderStateReducer,
} from "./AddOrderReducer";
import { OrderResultList } from "./OrderResultList";
import { SearchCustomerOrders } from "./SearchCustomerOrders";

interface AddOrderModalProps {
  isOpen: boolean;
  closeModal: () => void;
  associatedOrders: string[];
  filterAssociated: boolean;
}

export const AddOrderModal = ({
  isOpen,
  closeModal,
  associatedOrders,
  filterAssociated,
}: AddOrderModalProps) => {
  const { updateToast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const { opportunity } = useActiveProject();
  const { customer, isValidating: isValidatingCustomer } = useCustomer(
    opportunity?.id
  );
  const { data: customerOrders, isValidating: isLoadingCustomerOrders } =
    useSearchCustomerOrders(
      isValidatingCustomer,
      opportunity?.customerType,
      customer?.email,
      opportunity?.partyId
    );

  const [state, actions] = useTypesafeActions<
    AddOrderState,
    AddOrderStateActions
  >(
    addOrderStateReducer,
    { ...AddOrderStateDefault, defaultPartyId: opportunity?.partyId },
    addOrderStateActions
  );

  const showOrders = !state.searchValue || state.customerInfo;

  const ordersWithoutAppointments = customerOrders?.filter(
    (order) => order.clientSystem !== "BOKA"
  );

  const filteredUnassociatedOrders = filterAssociated
    ? ordersWithoutAppointments?.filter(
        (ord) => !associatedOrders.includes(ord.orderNo)
      )
    : ordersWithoutAppointments;

  const onClickSave = async () => {
    setIsSaving(true);
    try {
      if (state.orderNumbers && opportunity?.id !== undefined) {
        await associateOrders(state.orderNumbers, opportunity.id);
      }
      updateToast({
        id: state.orderNumbers?.join(","),
        title: t("prompt.orders_saved"),
      });
    } catch (error) {
      updateToast({
        id: state.orderNumbers?.join(","),
        title: t("prompt.order_associated_failed"),
        message: getDebugMessage(error),
        type: "error",
      });
    } finally {
      setIsSaving(false);
      closeModal();
    }
  };

  return (
    <ModalSheet
      id="add-order-modal"
      title={t("action.add_orders")}
      visible={isOpen}
      onClose={closeModal}
      onBack={
        state.customerInfo
          ? () => {
              actions.setCustomer(undefined);
            }
          : undefined
      }
      primaryButtonProps={{
        text:
          (state.orderNumbers?.length ?? 0) > 0
            ? t("action.add_order_total", { total: state.orderNumbers?.length })
            : t("prompt.no_new_orders_selected"),
        disabled:
          !opportunity?.id ||
          !state.orderNumbers ||
          state.orderNumbers.length === 0,
        loading: isSaving,
        onClick: () => {
          onClickSave();
          trackAnalytics("add_order");
        },
      }}
    >
      <SectionContainer>
        <SearchCustomerOrders state={state} actions={actions} />
        {showOrders && (
          <OrderResultList
            state={state}
            selectOrders={actions.setOrderNumbers}
            isLoading={isLoadingCustomerOrders}
            customerOrders={filteredUnassociatedOrders}
          />
        )}
      </SectionContainer>
    </ModalSheet>
  );
};
